import React, {
  FunctionComponent,
  useState,
  useEffect,
  useContext,
} from "react";
import {
  Alert,
  Choice,
  HelpDrawerToggle,
  HelpDrawer,
  CloseIconThin,
} from "@cmsgov/ds-medicare-gov";
import {
  CONSTANTS as C,
  sendTealiumEvent,
  useTranslate,
  isOutsideOpenEnrollment,
  parseSearchParams,
} from "../../helpers";
import CountyAndMaybeLis from "./CountyAndMaybeLis";
import { CoverageType, WhatToDoType } from "./types";
import { Continue } from "../../components";
import { withRouter, RouteComponentProps } from "react-router";
import URI from "urijs";
import LearnMore from "./LearnMore";
import { Beneficiary, ActionType } from "../../@types";
import { AppStore } from "../../app/store";
import { TealiumConversions } from "../../helpers/CONSTANTS";
import PlanQuestions from "./PlanQuestions";
import { FormattedMessage } from "react-intl-phraseapp";
import { useFlags } from "launchdarkly-react-client-sdk";
import OutsideOpenEnrollmentHelp from "./OutsideOpenEnrollmentHelp";
import OEYearAlert from "./OEYearAlert";
import { hasLoginSession } from "../../helpers/loginHelpers";
import states from "../EnrollmentFormPage/states";
import { createGa4Event, Ga4Event } from "../../helpers/analytics";

interface WhatMedicareCoverageProps extends RouteComponentProps {
  beneficiary?: Beneficiary;
}

const WhatMedicareCoverage: FunctionComponent<WhatMedicareCoverageProps> = ({
  location,
  beneficiary,
}) => {
  const t = useTranslate();
  const [coverageSelection, updateCoverageSelection] = useState<CoverageType>(
    CoverageType.selectionNotMade
  );
  const [showZip, setZipVisibility] = useState(false);
  const [showOoeHelpDrawer, setShowOoeHelpDrawer] = useState(false);
  const { state, dispatch } = useContext(AppStore.AppContext);
  const flags = useFlags();
  const {
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  } = flags;
  const outsideOpenEnrollment = isOutsideOpenEnrollment({
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  });

  const handleChoice = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const selection = e.target.value as CoverageType;

    /* istanbul ignore next: tested elsewhere */
    sendTealiumEvent(
      "Find Plans - Enter Your Information",
      TealiumConversions[selection]
    );

    createGa4Event({
      event_name: Ga4Event.COVERAGE_SELECTED,
      coverage_type: selection,
      state,
      flags,
    });

    dispatch({
      type: ActionType.UPDATE_VIEW_MEDIGAP,
      payload: selection === CoverageType.PDPAndMedigap,
    });

    updateCoverageSelection(selection);
  };

  const isMedigapPolicySelected =
    coverageSelection === CoverageType.MedigapPolicy;
  const isLearnMoreChecked = coverageSelection === CoverageType.learnMore;

  useEffect(() => {
    if (
      coverageSelection !== CoverageType.selectionNotMade &&
      coverageSelection !== CoverageType.MedigapPolicy &&
      coverageSelection !== CoverageType.learnMore
    ) {
      setZipVisibility(true);
    } else {
      setZipVisibility(false);
    }
  }, [coverageSelection]);

  useEffect(() => {
    if (
      state.cwCoverage &&
      state.cwCoverage !== CoverageType.OriginalMedicare
    ) {
      updateCoverageSelection(state.cwCoverage);
    } else {
      const uri = new URI(location.search);
      const { planType, learnMore } = parseSearchParams(uri.search(true));

      if (planType && planType !== CoverageType.OriginalMedicare) {
        updateCoverageSelection(planType as CoverageType);
      } else if (learnMore !== undefined) {
        updateCoverageSelection(CoverageType.learnMore);
      }
    }
  }, []);

  const learnMoreOption = (
    <Choice
      id="learn-more"
      type="radio"
      name="what-coverage"
      value={WhatToDoType.learnMore}
      onChange={handleChoice}
      checked={isLearnMoreChecked}
      checkedChildren={
        isMedigapPolicySelected ? (
          <>
            <Alert role="alert" hideIcon={true}>
              {t("landing_page.coverage_selection.medigap.text")}
            </Alert>
            <Continue to={C.medigapLink} useLink>
              {t("landing_page.coverage_selection.view_policies")}
            </Continue>
          </>
        ) : (
          undefined
        )
      }
    >
      {t("landing_page.learn_more.heading")}
    </Choice>
  );

  let fips = "";
  if (beneficiary) {
    const stateInfo = states.find(
      s => s.abbreviation === beneficiary.mailing_address.state
    );
    fips = stateInfo
      ? `${stateInfo.fipsCode}${beneficiary.mailing_address.county}`
      : "";
  }

  return (
    <div className="CoverageSelection">
      <fieldset className="ds-c-fieldset ds-u-margin--0">
        <legend className="ds-u-margin--0 ds-u-margin-bottom--1 ds-u-padding--0">
          <FormattedMessage
            tagName="h2"
            id="routing.what_type_of_coverage"
            values={{
              year: state.year,
            }}
          />
        </legend>

        {!mctYpOeNextYear && !outsideOpenEnrollment && <OEYearAlert />}

        {outsideOpenEnrollment && (
          <div className="e2e-ooe-alert">
            <Alert hideIcon={true} variation="warn">
              <span
                aria-label={
                  hasLoginSession() && state.beneficiary
                    ? t(
                        "what_coverage.outside_open_enrollment_alert_logged_in_no_link"
                      )
                    : t("what_coverage.outside_open_enrollment_alert_partdee")
                }
              >
                {hasLoginSession() && state.beneficiary
                  ? t(
                      "what_coverage.outside_open_enrollment_alert_logged_in_no_link"
                    )
                  : t("what_coverage.outside_open_enrollment_alert_partd")}
              </span>{" "}
              <HelpDrawerToggle
                inline
                helpDrawerOpen={showOoeHelpDrawer}
                showDrawer={(): void => setShowOoeHelpDrawer(true)}
              >
                {t("what_coverage.outside_open_enrollment_drawer_link")}
              </HelpDrawerToggle>
            </Alert>
          </div>
        )}

        {showOoeHelpDrawer && (
          <div className="mct-c-help-drawer">
            <HelpDrawer
              heading={t("ooe_help.heading")}
              onCloseClick={(): void => setShowOoeHelpDrawer(false)}
              closeButtonText={<CloseIconThin />}
              ariaLabel={t("close_help_drawer")}
            >
              <OutsideOpenEnrollmentHelp />
            </HelpDrawer>
          </div>
        )}

        {!state.cwCoverage && outsideOpenEnrollment && learnMoreOption}
        <PlanQuestions
          handleChoice={handleChoice}
          coverageSelection={coverageSelection}
        />
        {!state.cwCoverage && !outsideOpenEnrollment && learnMoreOption}

        {isLearnMoreChecked && (
          <>
            {beneficiary ? (
              <LearnMore
                fips={fips}
                zip={beneficiary.mailing_address.zipcode}
              />
            ) : (
              <LearnMore />
            )}
          </>
        )}
        {showZip && (
          <CountyAndMaybeLis
            coverageType={coverageSelection}
            beneficiary={beneficiary}
          />
        )}
      </fieldset>
    </div>
  );
};

export default withRouter(WhatMedicareCoverage);
